import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowPathIcon,
  CalendarIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";

import Toast from "../components/Toast";
import DrilldownDrawer from "../components/reports/DrilldownDrawer";
import KpiTiles from "../components/reports/KpiTiles";
import PlanLockedHint from "../components/PlanLockedHint";
import Popover, { MenuItem } from "../components/reports/Popover";
import ReportCharts from "../components/reports/ReportCharts";
import ReportFilterBar, { EXTRA_FILTER_KEYS } from "../components/reports/ReportFilterBar";
import ReportTable from "../components/reports/ReportTable";
import ScheduleModal from "../components/reports/ScheduleModal";
import { formatWindow, updatedLabel } from "../components/reports/reportFormat";
import { apiErrorMessage } from "../services/api";
import { humanise, labelFor } from "../services/enumLabels";
import { getCurrentUser } from "../services/authService";
import { hasFeature } from "../services/features";
import { can } from "../services/permissions";
import {
  downloadReportExport,
  exportReportV2,
  fetchReportCatalogue,
  fetchReportFilterOptions,
  runReport,
} from "../services/reportService";

const DEFAULT_PERIOD = "last_7_days";
const DEFAULT_COMPARE = "previous";
const EXTRA_KEYS = EXTRA_FILTER_KEYS;
const LIST_KEYS = ["branches", ...EXTRA_KEYS];
/** Filters that survive moving to another report; the rest are per report. */
const SHARED_KEYS = ["period", "from", "to", "branches", "currency", "compare"];

/** The old tabbed page (`/reports?tab=…`) → the new shell's defaults. */
const LEGACY_TABS = {
  dashboard: { category: "sales_finance", key: "daily_business_performance" },
  revenue: { category: "sales_finance", key: "daily_business_performance" },
  bookings: { category: "bookings", key: "booking_outcomes" },
  customers: { category: "customers" },
  loyalty: { category: "loyalty" },
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The applied filters, read from the URL (the only source of truth). */
const readFilters = (params) => {
  const list = (name) => (params.get(name) ? params.get(name).split(",").filter(Boolean) : []);
  const from = DATE.test(params.get("from") ?? "") ? params.get("from") : "";
  const to = DATE.test(params.get("to") ?? "") ? params.get("to") : "";
  const custom = Boolean(from && to);

  return {
    period: custom ? "" : params.get("period") || DEFAULT_PERIOD,
    from: custom ? from : "",
    to: custom ? to : "",
    branches: list("branches"),
    currency: params.get("currency") ?? "",
    compare: params.get("compare") || DEFAULT_COMPARE,
    refund_view: params.get("refund_view") ?? "",
    date_basis: params.get("date_basis") ?? "",
    inactive_days: params.get("inactive_days") ?? "",
    ...Object.fromEntries(EXTRA_KEYS.map((name) => [name, list(name)])),
  };
};

/** Filters → query string entries; defaults are left out. */
const writeFilters = (filters, params) => {
  const set = (name, value) => (value ? params.set(name, value) : params.delete(name));

  set("period", filters.period && filters.period !== DEFAULT_PERIOD ? filters.period : "");
  set("from", filters.period ? "" : filters.from);
  set("to", filters.period ? "" : filters.to);
  set("currency", filters.currency);
  set("compare", filters.compare !== DEFAULT_COMPARE ? filters.compare : "");
  set("refund_view", filters.refund_view);
  set("date_basis", filters.date_basis);
  set("inactive_days", filters.inactive_days);
  LIST_KEYS.forEach((name) => set(name, (filters[name] ?? []).join(",")));

  return params;
};

/** What the engine is asked for: the applied filters, minus empties. */
const apiFilters = (filters, groupBy) => {
  const out = {
    ...(filters.period ? { period: filters.period } : { from: filters.from, to: filters.to }),
    branches: filters.branches,
    currency: filters.currency,
    compare: filters.compare,
    refund_view: filters.refund_view,
    date_basis: filters.date_basis,
    inactive_days: filters.inactive_days,
    group_by: groupBy,
  };

  EXTRA_KEYS.forEach((name) => {
    if (filters[name]?.length) out[name] = filters[name];
  });

  Object.keys(out).forEach((name) => {
    const value = out[name];
    if (
      value === "" ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete out[name];
    }
  });

  return out;
};

const sameFilters = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Keep the filters every report shares when moving to another one. */
const sharedQuery = (params) => {
  const next = new URLSearchParams();
  SHARED_KEYS.forEach((name) => {
    if (params.get(name)) next.set(name, params.get(name));
  });

  return next.toString() ? `?${next}` : "";
};

/** Drill points whose value is an enum: how to name them on a deep link. */
const DRILL_KINDS = {
  method: "payment_method",
  reason: "refund_reason",
  type: "stock_movement",
  status: "booking_status",
  outcome: "booking_status",
  source: "booking_source",
};

/**
 * Reports v2 — the Reports page (spec 02, layout `ref/reports.jpg`).
 *
 * Category pills → a report list on the left → one report card on the right
 * (filter bar, KPI tiles, charts, results table). Every number is the engine's
 * (`GET /business/reports/{key}`): the page formats, it never adds up.
 *
 * ## State lives in the URL
 *
 * `/reports/:category/:reportKey?period=…&branches=…&currency=…&compare=…` —
 * a deep link restores the report as it was, and the drilldown is `?drill=`,
 * so Back closes it and keeps the filters. The filter bar edits a draft;
 * nothing is fetched until Apply writes it to the URL.
 */
export default function Reports() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { category: categoryParam, reportKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = getCurrentUser();
  const mayExport = can(user, "reports.export");
  const maySchedule = can(user, "reports.schedule");
  // Export and scheduled emails are the plan's `reports_export`: without it
  // both become an upgrade hint (the API answers 403 plan_feature_unavailable).
  const exportInPlan = hasFeature(user, "reports_export");

  const [catalogue, setCatalogue] = useState(null);
  const [catalogueError, setCatalogueError] = useState("");
  const [options, setOptions] = useState(null);
  const [toast, setToast] = useState(null);

  /* ------------------------------------------------- catalogue + options */

  const loadCatalogue = useCallback(async () => {
    setCatalogueError("");
    try {
      const [cat, opts] = await Promise.all([
        fetchReportCatalogue(),
        fetchReportFilterOptions().catch(() => null),
      ]);
      setCatalogue(cat);
      setOptions(opts);
    } catch (err) {
      setCatalogueError(apiErrorMessage(err, t("reports.loadFailed")));
    }
  }, [t]);

  useEffect(() => {
    loadCatalogue();
  }, [loadCatalogue, i18n.language]);

  const reports = useMemo(
    () => [...(catalogue?.reports ?? [])].sort((a, b) => a.number - b.number),
    [catalogue]
  );
  const categories = useMemo(() => catalogue?.categories ?? [], [catalogue]);
  const report = reports.find((r) => r.key === reportKey) ?? null;

  /* ----------------------------------------------- default / legacy URLs */

  useEffect(() => {
    if (!catalogue) return;

    // Old tabbed URLs land on the new defaults, keeping their range/branch.
    const legacy = LEGACY_TABS[searchParams.get("tab")];
    if (legacy || searchParams.has("tab") || searchParams.has("branch")) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      if (next.get("branch")) next.set("branches", next.get("branch"));
      next.delete("branch");
      const target = legacy?.key ?? reportKey;
      const cat = legacy?.category ?? categoryParam;
      const path = target ? `/reports/${cat}/${target}` : cat ? `/reports/${cat}` : "/reports";
      navigate(`${path}${next.toString() ? `?${next}` : ""}`, { replace: true });
      return;
    }

    if (report && report.category !== categoryParam) {
      navigate(`/reports/${report.category}/${report.key}${location.search}`, { replace: true });
      return;
    }

    if (!reportKey) {
      const cat = categories.find((c) => c.key === categoryParam) ?? categories[0];
      if (!cat) return;
      const inCategory = reports.filter((r) => r.category === cat.key);
      const first = inCategory.find((r) => r.available && !r.plan_locked) ?? inCategory[0];
      if (first) navigate(`/reports/${cat.key}/${first.key}${location.search}`, { replace: true });
    }
  }, [
    catalogue,
    report,
    reports,
    categories,
    categoryParam,
    reportKey,
    searchParams,
    location.search,
    navigate,
  ]);

  // Opened from an "export ready" notification (RPT-01): fetch the file once.
  const exportId = searchParams.get("export");

  useEffect(() => {
    if (!exportId) return;

    downloadReportExport(exportId)
      .then((filename) => setToast({ type: "success", message: t("reports.exported", { filename }) }))
      .catch((err) => setToast({ type: "error", message: apiErrorMessage(err, t("reports.exportExpired")) }))
      .finally(() => {
        const next = new URLSearchParams(searchParams);
        next.delete("export");
        setSearchParams(next, { replace: true });
      });
    // Only when the id changes; searchParams is read, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportId]);

  /* ------------------------------------------------------------ filters */

  const appliedKey = useMemo(() => JSON.stringify(readFilters(searchParams)), [searchParams]);
  const applied = useMemo(() => JSON.parse(appliedKey), [appliedKey]);
  const [draft, setDraft] = useState(applied);

  // Whatever the URL says (Apply, Back, a deep link) resets the draft.
  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  const groupBy = searchParams.get("group_by") ?? "";
  const sort = searchParams.get("sort") ?? "";
  const direction = searchParams.get("direction") === "desc" ? "desc" : "asc";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const drill = searchParams.get("drill") ?? "";

  const draftValid =
    Boolean(draft.period) || (DATE.test(draft.from) && DATE.test(draft.to) && draft.from <= draft.to);
  const dirty = draftValid && !sameFilters(draft, applied);

  const updateParams = useCallback(
    (mutate, { push = false, state } = {}) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace: !push, state });
    },
    [searchParams, setSearchParams]
  );

  const apply = () =>
    updateParams((next) => {
      writeFilters(draft, next);
      next.delete("page");
    });

  /* ------------------------------------------------------------- run */

  const [result, setResult] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error | forbidden | soon | plan
  const [runError, setRunError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const sequence = useRef(0);
  // The table a KPI tile opens (AC01).
  const tableRef = useRef(null);

  const requestFilters = useMemo(() => apiFilters(applied, groupBy), [applied, groupBy]);

  const run = useCallback(
    // `quiet`: ask again in the background, keeping the figures on screen.
    async ({ refresh = false, quiet = false } = {}) => {
      if (!report) return;

      if (!report.available) {
        setStatus("soon");
        setResult(null);
        return;
      }

      // Not in the plan (inventory reports without inventory): the upgrade note, no request.
      if (report.plan_locked) {
        setStatus("plan");
        setResult(null);
        return;
      }

      const id = ++sequence.current;
      if (refresh) setRefreshing(true);
      else if (!quiet) setStatus("loading");
      setRunError("");

      try {
        const data = await runReport(report.key, requestFilters, {
          page,
          sort: sort || undefined,
          direction,
          refresh,
        });
        if (id !== sequence.current) return;
        setResult(data);
        setStatus("ready");
      } catch (err) {
        if (id !== sequence.current) return;
        const code = err?.response?.status;
        setResult(null);
        const planLocked = err?.response?.data?.code === "plan_feature_unavailable";
        setStatus(planLocked ? "plan" : code === 403 ? "forbidden" : code === 409 ? "soon" : "error");
        setRunError(apiErrorMessage(err, t("reports.loadFailed")));
      } finally {
        if (id === sequence.current) setRefreshing(false);
      }
    },
    // i18n.language: the engine answers in the request locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      report?.key,
      report?.available,
      report?.plan_locked,
      requestFilters,
      page,
      sort,
      direction,
      t,
      i18n.language,
    ]
  );

  useEffect(() => {
    run();
  }, [run]);

  // Back to this tab: ask again. The server recomputes only if something
  // changed, so this is cheap and the figures are never older than the data.
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && run({ quiet: true });
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [run]);

  // "Just now" turns into the time after a minute: re-render every 30 s.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const meta = result?.meta;

  /* ---------------------------------------------------------- actions */

  const openReport = (target) =>
    navigate(`/reports/${target.category}/${target.key}${sharedQuery(searchParams)}`);
  const openCategory = (key) => navigate(`/reports/${key}${sharedQuery(searchParams)}`);

  // The label travels with the history entry: the drawer's subtitle is what
  // was clicked ("Cash", "1 Sep"), not the point's key ("method:cash").
  const openDrill = (point, label) =>
    updateParams((next) => next.set("drill", point), {
      push: true,
      state: { drill: true, drillLabel: typeof label === "string" ? label : undefined },
    });

  const closeDrill = useCallback(() => {
    if (location.state?.drill) navigate(-1);
    else updateParams((next) => next.delete("drill"));
  }, [location.state, navigate, updateParams]);

  const [exporting, setExporting] = useState(false);

  const runExport = async (format) => {
    setExporting(true);
    try {
      const outcome = await exportReportV2(report.key, format, {
        ...requestFilters,
        ...(sort ? { sort, direction } : {}),
      });
      setToast(
        outcome.queued
          ? { type: "success", message: t("reports.exportQueued") }
          : outcome.filename
            ? { type: "success", message: t("reports.exported", { filename: outcome.filename }) }
            : null
      );
    } catch (err) {
      let message = apiErrorMessage(err, t("reports.exportFailed"));
      // Errors on a blob request arrive as a Blob; read the API's message out of it.
      if (err?.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await err.response.data.text()).error ?? message;
        } catch {
          // keep the generic message
        }
      }
      setToast({ type: "error", message });
    } finally {
      setExporting(false);
    }
  };

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const scheduleFilters = useMemo(() => ({ ...applied, group_by: groupBy }), [applied, groupBy]);

  /* ------------------------------------------------------------ render */

  const drillLabel = useMemo(() => {
    if (!drill) return "";
    if (location.state?.drillLabel) return location.state.drillLabel;

    // A deep link: name the point from its key, never show the raw key.
    const [dimension, ...rest] = drill.split(":");
    const value = rest.join(":");
    if (value === "none") return t("reports.unattributed");
    if (dimension === "branch")
      return options?.branches?.find((b) => b.id === value)?.name ?? t("reports.shell.drillPoint.branch");

    const kind = DRILL_KINDS[dimension];
    if (kind) return labelFor(kind, value);
    // Day / month buckets ("2026-09-01", "2026-09") format as dates.
    if (/^\d{4}-\d{2}(-\d{2})?$/.test(value)) return value;

    return t(`reports.shell.drillPoint.${dimension}`, { defaultValue: humanise(dimension) });
  }, [drill, location.state, options, t]);

  if (catalogueError) {
    return (
      <PageShell>
        <ErrorState message={catalogueError} onRetry={loadCatalogue} />
      </PageShell>
    );
  }

  if (!catalogue) {
    return (
      <PageShell>
        <div className="h-12 w-full max-w-3xl animate-pulse rounded-xl bg-raised" />
        <div className="mt-4 grid gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
          <div className="hidden h-96 animate-pulse rounded-card bg-raised lg:block" />
          <ReportSkeleton />
        </div>
      </PageShell>
    );
  }

  const activeCategory = report?.category ?? categoryParam ?? categories[0]?.key;

  return (
    <PageShell>
      {/* Category pills — only categories with a report this reader may open. */}
      <nav
        className="card mb-4 flex w-fit max-w-full flex-wrap gap-1 p-1.5"
        aria-label={t("reports.shell.categories")}
      >
        {categories.map((category) => (
          <button
            key={category.key}
            type="button"
            onClick={() => openCategory(category.key)}
            aria-current={category.key === activeCategory ? "page" : undefined}
            className={`whitespace-nowrap rounded-control px-4 py-2 text-dense transition-colors duration-fast ${
              category.key === activeCategory
                ? "bg-accent-wash font-bold text-accent-text"
                : "text-ink hover:bg-raised"
            }`}
          >
            {category.title}
          </button>
        ))}
      </nav>

      {categories.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={LockClosedIcon}
            title={t("reports.shell.noReportsTitle")}
            body={t("reports.shell.noReportsBody")}
          />
        </div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
          <ReportList
            reports={reports}
            categories={categories}
            activeCategory={activeCategory}
            activeKey={report?.key}
            totals={catalogue.totals}
            onOpen={openReport}
          />

          <section className="card min-w-0 p-4 sm:p-5">
            {!report ? (
              reportKey ? (
                <EmptyState
                  icon={LockClosedIcon}
                  title={t("reports.shell.forbiddenTitle")}
                  body={t("reports.shell.forbiddenBody")}
                />
              ) : (
                <ReportSkeleton bare />
              )
            ) : (
              <>
                {/* Title, basis, Schedule + Export */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-section-title font-bold">{report.title}</h2>
                    <p className="mt-0.5 text-dense text-ink-2">
                      {meta?.report?.description ?? report.description}{" "}
                      {report.finance &&
                        meta &&
                        (meta.currency
                          ? t("reports.shell.amountsIn", { currency: meta.currency })
                          : t("reports.shell.amountsPerCurrency"))}
                    </p>
                  </div>

                  {report.available && !report.plan_locked && status !== "forbidden" && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {maySchedule && !exportInPlan && (
                        <PlanLockedHint feature="reports_export" label={t("plan.schedule")} />
                      )}
                      {mayExport && !exportInPlan && <PlanLockedHint feature="reports_export" />}
                      {maySchedule && exportInPlan && (
                        <button
                          type="button"
                          className="btn-secondary flex items-center gap-2 text-dense"
                          onClick={() => setScheduleOpen(true)}
                        >
                          <CalendarIcon className="h-4 w-4" aria-hidden="true" />
                          {t("reports.shell.scheduleButton")}
                        </button>
                      )}
                      {mayExport && exportInPlan && (
                        <Popover
                          variant="primary"
                          label={exporting ? t("reports.exporting") : t("reports.export")}
                          disabled={exporting || status !== "ready"}
                          align="end"
                          panelClassName="w-44"
                        >
                          {(close) =>
                            [
                              ["xlsx", TableCellsIcon],
                              ["csv", DocumentTextIcon],
                              ["pdf", DocumentArrowDownIcon],
                            ].map(([format, Icon]) => (
                              <MenuItem
                                key={format}
                                onClick={() => {
                                  close();
                                  runExport(format);
                                }}
                              >
                                <Icon className="h-4 w-4 text-ink-2" aria-hidden="true" />
                                {t(`reports.shell.exportFormats.${format}`)}
                              </MenuItem>
                            ))
                          }
                        </Popover>
                      )}
                    </div>
                  )}
                </div>

                {report.available && !report.plan_locked && status !== "forbidden" && (
                  <div className="mt-4">
                    <ReportFilterBar
                      draft={draft}
                      setDraft={setDraft}
                      options={options}
                      report={report}
                      meta={meta}
                      dirty={dirty}
                      onApply={apply}
                      pinnedToday={report.access === "today"}
                    />
                    <MetaLine
                      meta={meta}
                      now={now}
                      refreshing={refreshing}
                      onRefresh={() => run({ refresh: true })}
                    />
                  </div>
                )}

                <div className="mt-4 space-y-4">
                  <Banners meta={status === "ready" ? meta : null} report={report} />

                  {status === "plan" ? (
                    <EmptyState
                      icon={LockClosedIcon}
                      title={t("plan.reportLockedTitle")}
                      body={t("plan.reportLockedBody")}
                    />
                  ) : status === "soon" ? (
                    <EmptyState
                      icon={CalendarIcon}
                      title={t("reports.shell.comingSoonTitle")}
                      body={report.unavailable_reason || t("reports.shell.comingSoonBody")}
                    />
                  ) : status === "forbidden" ? (
                    <EmptyState
                      icon={LockClosedIcon}
                      title={t("reports.shell.forbiddenTitle")}
                      body={t("reports.shell.forbiddenBody")}
                    />
                  ) : status === "error" ? (
                    <ErrorState message={runError} onRetry={() => run()} />
                  ) : status !== "ready" || !result ? (
                    <ReportSkeleton bare />
                  ) : (
                    <>
                      <KpiTiles
                        kpis={result.kpis}
                        onSelect={(kpi) => {
                          const column = (result.table?.columns ?? []).find(
                            (c) => c.key === kpi.key && c.sortable
                          );
                          if (column) {
                            updateParams((next) => {
                              next.set("sort", column.key);
                              next.set("direction", "desc");
                              next.delete("page");
                            });
                          }
                          tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                          tableRef.current?.focus({ preventScroll: true });
                        }}
                      />
                      <ReportCharts charts={result.charts} onPoint={openDrill} />
                      <div ref={tableRef} tabIndex={-1} className="outline-none">
                        <ReportTable
                          reportKey={report.key}
                          userId={user?.id}
                          table={result.table}
                          multiCurrency={report.finance && !meta?.currency}
                          groupBy={result.table?.group_by ?? groupBy}
                          onGroupBy={(value) =>
                            updateParams((next) => {
                              next.set("group_by", value);
                              next.delete("page");
                              next.delete("sort");
                              next.delete("direction");
                            })
                          }
                          sort={sort}
                          direction={direction}
                          onSort={(column, dir) =>
                            updateParams((next) => {
                              next.set("sort", column);
                              next.set("direction", dir);
                              next.delete("page");
                            })
                          }
                          onPage={(p) =>
                            updateParams((next) =>
                              p > 1 ? next.set("page", String(p)) : next.delete("page")
                            )
                          }
                          onRow={openDrill}
                          notes={meta?.notes ?? []}
                        />
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {drill && report?.available && (
        <DrilldownDrawer
          reportKey={report.key}
          point={drill}
          label={drillLabel}
          filters={requestFilters}
          onClose={closeDrill}
        />
      )}

      {report?.available && (
        <ScheduleModal
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          report={report}
          filters={scheduleFilters}
          periods={options?.periods ?? []}
          onSaved={() => setToast({ type: "success", message: t("reports.shell.schedule.saved") })}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </PageShell>
  );
}

/* --------------------------------------------------------------- pieces */

function PageShell({ children }) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0">
      <div className="mb-4">
        <h1 className="text-page-title font-bold">{t("reports.title")}</h1>
        <p className="text-body text-ink-2">{t("reports.subtitle")}</p>
      </div>
      {children}
    </div>
  );
}

/**
 * The category's report list (left panel). The search looks across every
 * category. Below `lg` it collapses to a dropdown above the report card.
 */
function ReportList({ reports, categories, activeCategory, activeKey, totals, onOpen }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? reports.filter((r) => `${r.title} ${r.description ?? ""} ${r.code}`.toLowerCase().includes(needle))
    : reports.filter((r) => r.category === activeCategory);
  const categoryTitle = (key) => categories.find((c) => c.key === key)?.title ?? key;

  return (
    <>
      {/* Tablet / mobile: one dropdown with every report, grouped. */}
      <div className="lg:hidden">
        <label className="sr-only" htmlFor="report-picker">
          {t("reports.shell.pickReport")}
        </label>
        <select
          id="report-picker"
          className="field-input"
          value={activeKey ?? ""}
          onChange={(e) => {
            const target = reports.find((r) => r.key === e.target.value);
            if (target) onOpen(target);
          }}
        >
          {categories.map((category) => (
            <optgroup key={category.key} label={category.title}>
              {reports
                .filter((r) => r.category === category.key)
                .map((r) => (
                  <option key={r.key} value={r.key} disabled={!r.available}>
                    {r.title}
                    {r.available ? "" : ` (${t("reports.shell.comingSoon")})`}
                    {r.available && r.plan_locked ? ` (${t("plan.reportLocked")})` : ""}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </div>

      <aside className="card hidden min-h-[32rem] flex-col p-4 lg:flex">
        <h2 className="text-section-title font-bold">
          {needle ? t("reports.shell.searchResults") : categoryTitle(activeCategory)}
        </h2>

        <div className="relative mt-3">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
            aria-hidden="true"
          />
          <input
            type="search"
            className="field-input ps-9"
            placeholder={t("reports.shell.findReport")}
            aria-label={t("reports.shell.findReport")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <ul className="mt-3 flex-1 space-y-0.5">
          {visible.length === 0 && (
            <li className="px-3 py-6 text-center text-dense text-ink-2">{t("reports.shell.noMatch")}</li>
          )}
          {visible.map((r) => {
            const active = r.key === activeKey;

            return (
              <li key={r.key}>
                <button
                  type="button"
                  disabled={!r.available}
                  onClick={() => onOpen(r)}
                  aria-current={active ? "page" : undefined}
                  title={
                    r.available ? r.description : r.unavailable_reason || t("reports.shell.comingSoonBody")
                  }
                  className={`flex w-full items-center gap-2 rounded-control px-3 py-2 text-start text-dense transition-colors duration-fast ${
                    active
                      ? "bg-accent-wash font-bold text-accent-text"
                      : r.available
                        ? "text-ink hover:bg-raised"
                        : "cursor-not-allowed text-ink-muted"
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{r.title}</span>
                    {needle && (
                      <span className="block truncate text-caption font-normal text-ink-2">
                        {categoryTitle(r.category)}
                      </span>
                    )}
                  </span>
                  {!r.available && (
                    <span className="shrink-0 rounded-full bg-raised px-2 py-0.5 text-caption text-ink-2">
                      {t("reports.shell.comingSoon")}
                    </span>
                  )}
                  {r.available && r.plan_locked && (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-raised px-2 py-0.5 text-caption text-ink-2"
                      title={t("plan.reportLockedTitle")}
                    >
                      <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
                      {t("plan.reportLocked")}
                    </span>
                  )}
                  {active && (
                    <ChevronRightIcon className="h-4 w-4 shrink-0 rtl:rotate-180" aria-hidden="true" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {totals && (
          <p className="mt-4 text-caption text-ink-2">
            {t("reports.shell.catalogueTotals", { reports: totals.reports, categories: totals.categories })}
          </p>
        )}
      </aside>
    </>
  );
}

/**
 * "Updated just now · Asia/Amman · Date: payment date · Compared with …",
 * and Refresh (recompute, skipping the cache).
 */
function MetaLine({ meta, now, refreshing, onRefresh }) {
  const { t, i18n } = useTranslation();

  if (!meta) return <div className="mt-2 h-4" />;

  const updated = updatedLabel(meta.as_of, meta.cached, now, i18n.language, t);

  const bases = Object.entries(meta.date_basis ?? {});
  const basisLabel = (key) => labelFor("date_basis", key);
  const basis =
    bases.length === 1
      ? t("reports.shell.dateBasisOne", { basis: basisLabel(bases[0][0]) })
      : bases.length > 1
        ? t("reports.shell.dateBasisMany", {
            list: bases
              .map(([key, what]) =>
                t("reports.shell.datedBy", {
                  what: t(`reports.shell.dated.${what}`, { defaultValue: humanise(what) }),
                  basis: basisLabel(key),
                })
              )
              .join(" · "),
          })
        : "";

  const parts = [
    updated,
    meta.timezone,
    basis,
    meta.compare_window
      ? t("reports.shell.comparedWith", { window: formatWindow(meta.compare_window, i18n.language) })
      : "",
  ].filter(Boolean);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="text-caption text-ink-2">{parts.join(" · ")}</p>
      <button
        type="button"
        className="flex items-center gap-1 text-caption font-bold text-accent-text disabled:opacity-60"
        onClick={onRefresh}
        disabled={refreshing}
      >
        <ArrowPathIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
        {t("reports.shell.refresh")}
      </button>
    </div>
  );
}

function Banners({ meta, report }) {
  const { t } = useTranslation();

  if (!meta) return null;

  const banner = "flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-dense";

  return (
    <>
      {meta.scope?.no_branch_access && (
        <div className={`${banner} border-warning-line bg-warning-wash text-warning-text`}>
          {t("reports.noBranchAccess")}
        </div>
      )}
      {report.access === "today" && (
        <div className={`${banner} border-info-line bg-info-wash text-info-text`}>
          {t("reports.shell.todayOnly")}
        </div>
      )}
      {report.access === "self" && (
        <div className={`${banner} border-info-line bg-info-wash text-info-text`}>
          {t("reports.shell.selfOnly")}
        </div>
      )}
      {meta.scope?.restricted && !meta.scope?.no_branch_access && (
        <div className={`${banner} border-accent-line bg-accent-wash text-accent-text`}>
          {t("reports.scopeRestricted")}
        </div>
      )}
      {(meta.other_branch_timezones ?? []).length > 0 && (
        <div className={`${banner} border-warning-line bg-warning-wash text-warning-text`}>
          {t("reports.shell.otherTimezones", {
            timezone: meta.timezone,
            others: meta.other_branch_timezones.join(", "),
          })}
        </div>
      )}
    </>
  );
}

function ReportSkeleton({ bare = false }) {
  const block = "animate-pulse rounded-card bg-raised";
  const body = (
    <div className="space-y-4" aria-busy="true">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={`${block} h-24`} />
        ))}
      </div>
      <div className="grid gap-3 xl:grid-cols-[3fr_2fr]">
        <div className={`${block} h-64`} />
        <div className={`${block} h-64`} />
      </div>
      <div className={`${block} h-80`} />
    </div>
  );

  return bare ? body : <div className="card p-5">{body}</div>;
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <span className="grid h-12 w-12 place-items-center rounded-full bg-raised text-ink-2">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </span>
      <p className="mt-3 text-card-title font-bold">{title}</p>
      {body && <p className="mt-1 max-w-md text-dense text-ink-2">{body}</p>}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-danger-line bg-danger-wash px-4 py-4 text-dense text-danger-text">
      <p>{message || t("reports.loadFailed")}</p>
      <button type="button" className="btn-secondary mt-3" onClick={onRetry}>
        {t("reports.shell.retry")}
      </button>
    </div>
  );
}
